/// <reference types="react" />

import * as React from "react";

import * as ComponentBase from "DistributedTaskControls/Common/Components/Base";
import { GraphLayoutHelper } from "DistributedTaskControls/Components/Canvas/GraphLayoutHelper";
import { ProcessVariablesV2Store } from "DistributedTaskControls/Variables/ProcessVariablesV2/DataStore";

import { CreateReleaseControllerView } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/CreateReleaseControllerView";
import { CreateReleaseEnvironmentNodeConstants } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/Constants";
import { CreateReleaseStore } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/CreateReleaseStore";
import { EnvironmentListStore } from "PipelineWorkflow/Scripts/Shared/EnvironmentList/EnvironmentListStore";
import { EnvironmentUtils } from "PipelineWorkflow/Scripts/Shared/Utils/EnvironmentUtils";
import { PipelineRelease, PipelineDefinitionEnvironment, PipelineDefinition, PipelineEnvironment } from "PipelineWorkflow/Scripts/Common/Types";
import { ProgressIndicatorStore } from "PipelineWorkflow/Scripts/Common/Stores/ProgressIndicatorStore";
import * as Resources from "PipelineWorkflow/Scripts/Resources/TFS.Resources.PipelineWorkflow";

import { Panel } from "VSSUI/Panel";
import { ContentSize } from "VSSUI/Callout";
import { css } from "VSSUI/Util";

export interface ICreateReleasePanelState {
    showPanel: boolean;
    width: number;
}

export interface ICreateReleasePanelProps<T extends PipelineDefinition | PipelineRelease, P extends PipelineDefinitionEnvironment | PipelineEnvironment> extends ComponentBase.IProps {
    createReleaseStore: CreateReleaseStore<T, P>;
    progressStore: ProgressIndicatorStore;
    environmentListStore: EnvironmentListStore<P>;
    variablesListStore: ProcessVariablesV2Store;
    hasCloseButton?: boolean;
    onClose?: () => void;
    elementToFocusOnDismiss?: HTMLElement;
    minWidth?: number;
    onQueueRelease?: (pipelineRelease: PipelineRelease, projectName?: string) => void;
    startReleaseMode?: boolean;
    buildDefinitionId?: string;
}

/**
 * Renders Create release panel
 */
export class CreateReleasePanel<T extends PipelineDefinition | PipelineRelease, P extends PipelineDefinitionEnvironment | PipelineEnvironment> extends ComponentBase.Component<ICreateReleasePanelProps<T, P>, ICreateReleasePanelState> {

    constructor(props: ICreateReleasePanelProps<T, P>) {
        super(props);
        this.state = {
            showPanel: true,
            width: this._getMinWidth()
        };
        this._columnWidth = CreateReleaseEnvironmentNodeConstants.compactEnvironmentNodeWidth +
            CreateReleaseEnvironmentNodeConstants.createReleaseEnvironmentCanvasHorizontalMargin;
    }

    public render(): JSX.Element {
        const storeData = this.props.createReleaseStore.getState().data;
        const sizeClassName = this.state.width <= CreateReleasePanel._defaultMinWidth ?
            "cr-rel-xs" :
            (this.state.width <= CreateReleasePanel._defaultSmallWidth ?
                "cr-rel-s" : (this.state.width <= CreateReleasePanel._defaultMediumWidth ?
                    "cr-rel-m" : "cr-rel-l"));

        return (
            <div>
                {
                    this.state.showPanel &&
                    <Panel
                        onDismiss={this._closePanel}
                        titleProps={{ text: this.props.startReleaseMode ? Resources.CreateReleasePanelStartReleaseLabel : Resources.CreateReleasePanelHeaderLabel }}
                        description={storeData && storeData.name}
                        modal={true}
                        className={"create-release-panel"}
                        contentClassName={css("create-release-panel-content", sizeClassName)}
                        elementToFocusOnDismiss={this.props.elementToFocusOnDismiss}>
                        <CreateReleaseControllerView
                            createReleaseStore={this.props.createReleaseStore}
                            progressStore={this.props.progressStore}
                            environmentListStore={this.props.environmentListStore}
                            variablesListStore={this.props.variablesListStore}
                            onCloseClick={this._closePanel}
                            onQueueRelease={this.props.onQueueRelease}
                            onDidUpdate={this._setPanelWidth}
                            startReleaseMode={this.props.startReleaseMode}
                            buildDefinitionId={this.props.buildDefinitionId}>
                        </CreateReleaseControllerView>
                    </Panel>
                }
            </div>);
    }

    private _closePanel = () => {
        if (this.state && this.state.showPanel) {
            this.setState({ showPanel: false });
        }

        this._handleOnClosed();
    }

    private _handleOnClosed = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    private _setPanelWidth = (): void => {
        if (this.props.environmentListStore) {
            const edges = this.props.environmentListStore.getEnvironmentConnections();
            const environmentsData = this.props.environmentListStore.getEnvironmentsData();
            const nodes = EnvironmentUtils.getNodes(environmentsData, null);
            const stagingOrderAndDependencies = GraphLayoutHelper.createStagingOrderAndDependencies(nodes, edges);

            if (stagingOrderAndDependencies && stagingOrderAndDependencies.stagingOrder) {
                let numColumns = stagingOrderAndDependencies.stagingOrder.length;
                let columns = numColumns < CreateReleasePanel._maxColumnsToShow ? numColumns : CreateReleasePanel._maxColumnsToShow;
                let width = (columns + 1) * this._columnWidth;

                if (width > document.documentElement.clientWidth - 200) {
                    width = document.documentElement.clientWidth - 200;
                }

                let minWidth: number = this._getMinWidth();
                if (width < minWidth) {
                    width = minWidth;
                }

                this.setState({ width: width });
            }
        }
    }

    private _getMinWidth(): number {
        if (this.props.minWidth) {
            return this.props.minWidth;
        }

        return CreateReleasePanel._defaultMinWidth;
    }

    private _columnWidth: number;

    private static readonly _maxColumnsToShow: number = 10;
    private static readonly _defaultMinWidth: number = 630;
    private static readonly _defaultSmallWidth: number = 720;
    private static readonly _defaultMediumWidth: number = 1200;
}